import { useState } from "react";
import { Printer, Users } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StatTile from "@/components/shared/StatTile";
import SegmentedChoice from "@/components/shared/SegmentedChoice";
import { formatNumber, pluralize } from "@/lib/format";

export default function StudentActivationTile({ campaign, canRemind }) {
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [perPage, setPerPage] = useState("1");
  const activated = campaign.activated_student_count ?? 0;
  const remaining = (campaign.student_count ?? 0) - activated;
  const hasReminders = canRemind && remaining > 0 && campaign.reminder_sheets_paths;

  return (
    <>
      <StatTile
        icon={Users}
        label="Students"
        value={formatNumber(campaign.student_count ?? 0)}
      >
        <div className="mt-1 flex flex-wrap items-center gap-x-1 whitespace-nowrap text-[11px] text-muted-foreground">
          <span>{formatNumber(activated)} activated</span>
          {hasReminders && (
            <span className="inline-flex items-center gap-1">
              <span aria-hidden="true">·</span>
              <Button variant="link" size="sm" className="relative h-auto p-0 text-[11px] leading-normal after:absolute after:-inset-y-2 after:inset-x-0" aria-label="Print reminders" aria-haspopup="dialog" onClick={() => setShowPrintOptions(true)}>
                Remind
              </Button>
            </span>
          )}
        </div>
      </StatTile>
      {hasReminders && (
        <Dialog open={showPrintOptions} onClose={() => setShowPrintOptions(false)} title="Print activation reminders" description={`Take-home sheets for ${pluralize(remaining, "student")} who haven’t activated yet, grouped by classroom.`} className="max-w-lg">
          <div className="space-y-4">
            <SegmentedChoice label="Students per page" value={perPage} onChange={setPerPage} options={[
              { value: "1", label: "1 student per page", hint: "Full sheet with your prize settings. No cutting." },
              { value: "2", label: "2 students per page", hint: "Half sheets without prizes. Cut once to save paper." },
            ]} />
            <p className="text-sm text-muted-foreground">Print single-sided to send one reminder home with each student.</p>
            <Button variant="outline" size="sm" asChild>
              <a href={campaign.reminder_sheets_paths[perPage]}><Printer className="h-4 w-4" aria-hidden="true" /> Download reminder PDF</a>
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
}
